/**
 * The donor-advised-fund door on the giving page (owner-directed 2026-08-18).
 *
 * Chariot's `<chariot-connect>` web component draws the DAFpay button and owns the whole
 * authorization flow: the donor picks their DAF sponsor and signs in *inside Chariot's modal*
 * — no DAF credential, amount edit, or sponsor session ever touches this bundle. Our part is
 * two small moves: hand the modal the amount the donor already chose on this page, and, when
 * the modal reports success, tell our API to capture the grant (the server then talks to
 * Chariot; this browser holds nothing but a session id).
 *
 * Donations only. This component is mounted by the giving page and nothing else — tickets can
 * never offer it (IRS Notice 2017-73: a DAF may not pay for anything with donor benefits).
 *
 * The component script loads from Chariot's CDN by their requirement (it must not be bundled
 * or self-hosted). If the CDN is unreachable, the DAF door simply does not render — card and
 * bank giving are unaffected.
 */
import { useEffect, useRef, useState } from "react";
import type { DafGrantResponse } from "@givehere/contracts";
import { ApiError, createDafGrant } from "./api.js";

const SCRIPT_SRC = "https://cdn.givechariot.com/chariot-connect.umd.js";

let scriptPromise: Promise<void> | null = null;
const loadChariotScript = (): Promise<void> => {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${SCRIPT_SRC}"]`)) {
      resolve();
      return;
    }
    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      // Cleared so a later mount can try again instead of caching the failure forever.
      scriptPromise = null;
      reject(new Error("Chariot Connect failed to load"));
    };
    document.head.appendChild(script);
  });
  return scriptPromise;
};

type DonationRequest = {
  amount: number;
  firstName?: string;
  lastName?: string;
  email?: string;
};

type ChariotConnectElement = HTMLElement & {
  onDonationRequest(provide: () => DonationRequest): void;
};

type ChariotSuccessDetail = {
  workflowSessionId?: string;
  grantIntent?: { amount?: number };
};

/** DAFs accept whole dollars only; a cents-bearing custom amount rounds to the nearest. */
const wholeDollarCents = (amountMinor: string): number =>
  Math.max(100, Math.round(Number(amountMinor) / 100) * 100);

export function DafPayButton({
  connectId,
  contextToken,
  amountMinor,
  donorToken,
  suggestedName,
  suggestedEmail,
  onCaptured,
}: {
  connectId: string;
  contextToken: string;
  /** The amount selected on the giving page, minor units; prefills Chariot's modal. */
  amountMinor: string;
  donorToken: string | null;
  suggestedName: string | null;
  suggestedEmail: string | null;
  onCaptured: (response: DafGrantResponse) => void;
}) {
  const host = useRef<HTMLDivElement | null>(null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  /**
   * The mounted element's callbacks must read the *current* page state — the donor changes the
   * amount after the button mounts — so everything mutable rides a ref the callbacks close over.
   */
  const latest = useRef({ amountMinor, donorToken, suggestedName, suggestedEmail });
  latest.current = { amountMinor, donorToken, suggestedName, suggestedEmail };

  useEffect(() => {
    let disposed = false;
    let element: ChariotConnectElement | null = null;

    loadChariotScript()
      .then(() => {
        if (disposed || !host.current) return;
        element = document.createElement("chariot-connect") as ChariotConnectElement;
        element.setAttribute("cid", connectId);

        // Chariot documents a <1s budget for this callback; it is synchronous on purpose.
        element.onDonationRequest(() => {
          const name = (latest.current.suggestedName ?? "").trim();
          const [firstName, ...rest] = name.split(/\s+/).filter(Boolean);
          return {
            amount: wholeDollarCents(latest.current.amountMinor),
            ...(firstName ? { firstName } : {}),
            ...(rest.length > 0 ? { lastName: rest.join(" ") } : {}),
            ...(latest.current.suggestedEmail ? { email: latest.current.suggestedEmail } : {}),
          };
        });

        element.addEventListener("CHARIOT_SUCCESS", (event) => {
          const detail = (event as CustomEvent<ChariotSuccessDetail>).detail;
          if (!detail?.workflowSessionId) return;
          setBusy(true);
          setNote(null);
          // The intent amount from Chariot's own event when present; our page state otherwise.
          const cents =
            detail.grantIntent?.amount && detail.grantIntent.amount > 0
              ? Math.round(detail.grantIntent.amount)
              : wholeDollarCents(latest.current.amountMinor);
          void createDafGrant(
            {
              contextToken,
              workflowSessionId: detail.workflowSessionId,
              amountMinor: String(cents),
            },
            latest.current.donorToken,
          )
            .then(onCaptured)
            .catch((caught: unknown) =>
              setNote(
                caught instanceof ApiError
                  ? caught.message
                  : // Shortened 2026-08-31, and the half that survives is the one that changes what
                    // a donor does next: not "we failed" but "it may already be in flight", which
                    // is what stops a second submission. The elaboration went; the instruction did
                    // not. `note` also carries server messages of arbitrary length, so this cannot
                    // become an ⓘ without the notice itself becoming a component.
                    "It may still be sent.",
              ),
            )
            .finally(() => setBusy(false));
        });

        host.current.appendChild(element);
      })
      .catch(() => setFailed(true));

    return () => {
      disposed = true;
      element?.remove();
    };
    // onCaptured deliberately rides `latest`-style stability: the parent passes a setState, so
    // it is not a dependency — re-running this effect would tear down and rebuild Chariot's
    // widget mid-grant.
  }, [connectId, contextToken]);

  if (failed) return null;

  return (
    <div className="dafpay">
      <div ref={host} className="dafpay__host" aria-busy={busy} />
      {busy && <p className="muted">Recording your grant…</p>}
      {note && (
        <p className="notice notice--error" role="alert">
          {note}
        </p>
      )}
    </div>
  );
}
